import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Map,
    AdvancedMarker,
    InfoWindow,
    useMap
} from "@vis.gl/react-google-maps";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import CrudService from 'services/cruds-service';
import { toast } from "react-toastify";
import RoomIcon from '@mui/icons-material/Room';
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import { Badge, Card, IconButton, Tooltip, Chip } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { DataGrid } from '@mui/x-data-grid';
import MDInput from 'components/MDInput';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { useMediaQuery } from "react-responsive";

// Custom components for shapes since @vis.gl doesn't have direct equivalents
export const CircleOverlay = ({ center, radius, options }) => {
    const map = useMap();
    useEffect(() => {
        if (!map) return;

        const circle = new google.maps.Circle({
            center,
            radius,
            ...options
        });
        circle.setMap(map);

        return () => circle.setMap(null);
    }, [map, center, radius, options]);
    return null;
};

export const RectangleOverlay = ({ bounds, options }) => {
    const map = useMap();
    useEffect(() => {
        if (!map) return;

        const rectangle = new google.maps.Rectangle({
            bounds,
            ...options
        });
        rectangle.setMap(map);

        return () => rectangle.setMap(null);
    }, [map, bounds, options]);
    return null;
};

export const PolygonOverlay = ({ paths, options }) => {
    const map = useMap();
    useEffect(() => {
        if (!map) return;

        const polygon = new google.maps.Polygon({
            paths,
            ...options
        });
        polygon.setMap(map);

        return () => polygon.setMap(null);
    }, [map, paths, options]);
    return null;
};

const containerStyle = {
    width: "100%",
    height: "400px",
};

const GEOFENCE_LIST_STORAGE_KEY = "rivio.geofenceList.tableSettings.v1";

const DEFAULT_GEOFENCE_LIST_TABLE_SETTINGS = {
  paginationModel: { page: 0, pageSize: 100 },
  sortModel: [{ field: "name", sort: "asc" }],
  filterModel: { items: [] },
  columnVisibilityModel: {
    id: true,
    status: true,
    entity: true,
    entityAddressItems: true,
    name: true,
    description: true,
    geofence_type: true,
    purpose: true,
    latitude: true,
    longitude: true,
    radius: true,
    color: true,
    priority: true,
    action: true,
  },
  searchQuery: "",
};

function readGeofenceListTableSettings() {
  try {
    return {
      ...DEFAULT_GEOFENCE_LIST_TABLE_SETTINGS,
      ...JSON.parse(localStorage.getItem(GEOFENCE_LIST_STORAGE_KEY) || "{}"),
    };
  } catch (error) {
    console.error("readGeofenceListTableSettings", error);
    return DEFAULT_GEOFENCE_LIST_TABLE_SETTINGS;
  }
}

function writeGeofenceListTableSettings(nextSettings) {
  localStorage.setItem(GEOFENCE_LIST_STORAGE_KEY, JSON.stringify(nextSettings));
}

const GeofenceManager = () => {
    const [geofences, setGeofences] = useState([]);
    const [searchGeofences, setSearchGeofences] = useState([]);
    const [selectedGeofence, setSelectedGeofence] = useState(null);
    const [defaultCenter, setDefaultCenter] = useState({ lat: 42.454272, lng: -88.249899 });
    const [defaultZoom, setDefaultZoom] = useState(10);
    const [data, setData] = useState([])
    const [tableData, setTableData] = useState([])
    const [loader, setLoader] = useState(false)
    
    const savedTableSettings = readGeofenceListTableSettings();
    const [paginationModel, setPaginationModel] = useState(savedTableSettings.paginationModel);
    const [sortModel, setSortModel] = useState(savedTableSettings.sortModel);
    const [filterModel, setFilterModel] = useState(savedTableSettings.filterModel);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(savedTableSettings.columnVisibilityModel);
    const [searchQuery, setSearchQuery] = useState(savedTableSettings.searchQuery);
    const [rowCount, setRowCount] = useState(0);

    const clustererRef = useRef(null);
    const zoomRef = useRef(null);
    const [markers, setMarkers] = useState({});
    const navigate = useNavigate()
    const map = useMap();
    const isTabletOrMobile = useMediaQuery({ query: "(max-width: 992px)" });

    function EntityAddressItemsCell({ value = [] }) {
        const visibleItems = value.slice(0, 2);
        const hiddenCount = Math.max(value.length - visibleItems.length, 0);
        const fullList = value.join("\n");

        if (!value.length) return <MDTypography variant="caption" color="text">--</MDTypography>;

        return (
            <Tooltip title={<pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{fullList}</pre>} placement="top" arrow>
                <MDBox display="flex" flexWrap="wrap" gap={0.5} sx={{ maxWidth: 260 }}>
                    {visibleItems.map((item) => (
                        <Chip key={item} label={item} size="small" variant="outlined" />
                    ))}
                    {hiddenCount > 0 && <Chip label={`+${hiddenCount} more`} size="small" color="info" variant="outlined" />}
                </MDBox>
            </Tooltip>
        );
    }

    useEffect(() => {
        ; (async () => {
            await getAllGeofences()
        })()
    }, [])

    useEffect(() => {
        const timeout = setTimeout(getAllGeofences, 500);
        return () => clearTimeout(timeout);
    }, [paginationModel, sortModel, filterModel, searchQuery]);

    async function getAllGeofences() {
        setLoader(true);
        try {
            const sortField = sortModel[0]?.field || "geofence_id";
            const sortOrder = sortModel[0]?.sort || "asc";
            const response = await CrudService.getGeofencesList({
                page: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                sortField,
                sortOrder,
                search: searchQuery.trim().toLowerCase(),
                filters: filterModel.items,
            })

            if (response.data) {
                setRowCount(response?.count || 0);
                setData(response.data)
                let geo = response.data.map(({ name, geofence_id, geofence_type, latitude, longitude, coordinates, radius, color }) => ({ name, geofence_id, geofence_type, latitude, longitude, coordinates, radius, color }))
                setGeofences(geo)
            }
        } catch (error) {
            console.error('getAllGeofences', error)
            toast.error(error.error);
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        setTableData(getRows(data))
    }, [data])

    useEffect(() => {
        writeGeofenceListTableSettings({
            paginationModel,
            sortModel,
            filterModel,
            columnVisibilityModel,
            searchQuery,
        });
    }, [paginationModel, sortModel, filterModel, columnVisibilityModel, searchQuery]);

    useEffect(() => {
        if (map && geofences.length > 0) {
            if (!clustererRef.current) {
                clustererRef.current = new MarkerClusterer({ map, algorithm: new SuperClusterAlgorithm({ maxZoom: 20 }) });
            }
            const bounds = new window.google.maps.LatLngBounds();
            geofences.forEach((geofence) => {
                if (geofence.geofence_type === "circle") {
                    const center = {
                        lat: parseFloat(geofence.latitude),
                        lng: parseFloat(geofence.longitude),
                    };
                    const radiusInDegrees = geofence.radius / 111320; // Rough conversion: 1 degree ~ 111.32km
                    bounds.extend({ lat: center.lat + radiusInDegrees, lng: center.lng });
                    bounds.extend({ lat: center.lat - radiusInDegrees, lng: center.lng });
                    bounds.extend({ lat: center.lat, lng: center.lng + radiusInDegrees });
                    bounds.extend({ lat: center.lat, lng: center.lng - radiusInDegrees });
                } else if (geofence.geofence_type === "rectangle" && geofence.coordinates.length === 2) {
                    bounds.extend(geofence.coordinates[0]);
                    bounds.extend(geofence.coordinates[1]);
                } else if (geofence.geofence_type === "polygon") {
                    geofence.coordinates.forEach((point) => bounds.extend(point));
                }
            });
            map.fitBounds(bounds, 50);
        }
    }, [geofences]);

    const searchIdsShownOnMap = (ids) => {
        if (ids.length) {
            let filterGeofence = geofences.filter((e) => ids.includes(e.geofence_id))
            if (filterGeofence.length > 0 && map) {
                setSearchGeofences(filterGeofence)
                const bounds = new window.google.maps.LatLngBounds();
                filterGeofence.forEach((geofence) => {
                    if (geofence.geofence_type === "circle") {
                        const center = {
                            lat: parseFloat(geofence.latitude),
                            lng: parseFloat(geofence.longitude),
                        };
                        const radiusInDegrees = geofence.radius / 111320; // Rough conversion: 1 degree ~ 111.32km
                        bounds.extend({ lat: center.lat + radiusInDegrees, lng: center.lng });
                        bounds.extend({ lat: center.lat - radiusInDegrees, lng: center.lng });
                        bounds.extend({ lat: center.lat, lng: center.lng + radiusInDegrees });
                        bounds.extend({ lat: center.lat, lng: center.lng - radiusInDegrees });
                    } else if (geofence.geofence_type === "rectangle" && geofence.coordinates.length === 2) {
                        bounds.extend(geofence.coordinates[0]);
                        bounds.extend(geofence.coordinates[1]);
                    } else if (geofence.geofence_type === "polygon") {
                        geofence.coordinates.forEach((point) => bounds.extend(point));
                    }
                });
                map.fitBounds(bounds, 50);
            }
        } else {
            setSearchGeofences([])
            if (map && geofences.length > 0) {
                const bounds = new window.google.maps.LatLngBounds();
                geofences.forEach((geofence) => {
                    if (geofence.geofence_type === "circle") {
                        const center = {
                            lat: parseFloat(geofence.latitude),
                            lng: parseFloat(geofence.longitude),
                        };
                        const radiusInDegrees = geofence.radius / 111320; // Rough conversion: 1 degree ~ 111.32km
                        bounds.extend({ lat: center.lat + radiusInDegrees, lng: center.lng });
                        bounds.extend({ lat: center.lat - radiusInDegrees, lng: center.lng });
                        bounds.extend({ lat: center.lat, lng: center.lng + radiusInDegrees });
                        bounds.extend({ lat: center.lat, lng: center.lng - radiusInDegrees });
                    } else if (geofence.geofence_type === "rectangle" && geofence.coordinates.length === 2) {
                        bounds.extend(geofence.coordinates[0]);
                        bounds.extend(geofence.coordinates[1]);
                    } else if (geofence.geofence_type === "polygon") {
                        geofence.coordinates.forEach((point) => bounds.extend(point));
                    }
                });
                map.fitBounds(bounds, 50);
            }
        }
    }

    const searchHandler = (value) => {
        setSearchQuery(value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }

    const getRows = (info) => {
        let updatedInfo = info.map((row) => {
            const entityAddressItemNames = (row?.EntityItemGeofenceAssociations ?? [])
                .map((association) => association?.EntityAddressItem?.name)
                .filter(Boolean);

            return {
                type: 'contacts',
                id: row.geofence_id,
                status: row?.StatusType?.status_name ?? '',
                entity: row?.Entity?.name ?? '',
                name: row.name,
                description: row.description,
                geofence_type: row.geofence_type ?? '',
                purpose: row?.GeofenceType?.type_name ?? '',
                latitude: row.latitude,
                longitude: row.longitude,
                radius: row.radius,
                color: row.color,
                priority: row.priority,
                entityAddressItems: entityAddressItemNames,
                entityAddressItemSearch: entityAddressItemNames.join(" "),
            }
        })
        return updatedInfo
    }

    const columns = [
        { headerName: 'ID', field: 'id', width: 70, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.row.id}</MDTypography> },
        {
            headerName: 'Status', field: 'status', sortable: false, renderCell: (info) => {
                return (
                    <Badge
                        color={info.value === 'Active' ? 'success' : 'error'}
                        variant={'standard'}
                        badgeContent={info.value}
                        style={{ marginLeft: '30px' }}
                    ></Badge>
                )
            }
        },
        { headerName: 'Entity', field: 'entity', width: 150, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        {
            headerName: 'Entity Address Item',
            field: 'entityAddressItems',
            width: 220,
            renderCell: ({value}) => <EntityAddressItemsCell value={value} />,
        },
        { headerName: 'Name', field: 'name', width: 200, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        { headerName: 'Description', field: 'description', width: 200, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        { headerName: 'Type', field: 'geofence_type', width: 120, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        { headerName: 'Purpose', field: 'purpose', renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        { headerName: 'Latitude', field: 'latitude', renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        { headerName: 'Longitude', field: 'longitude', renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        { headerName: 'Radius', field: 'radius', width: 100, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        {
            headerName: 'Color',
            field: 'color',
            width: 80,
            renderCell: (info) => (
                <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: info.value }} title={info.value}></div>
            )
        },
        { headerName: 'Priority', field: 'priority', width: 80, renderCell: (info) => <MDTypography component="div" fontSize={0.8}>{info.value}</MDTypography> },
        {
            headerName: 'Actions', field: 'action', sortable: false, width: 150, renderCell: (info) => (
                <MDBox display="flex" alignItems="center">
                    <Tooltip title="Edit Geofence">
                        <IconButton onClick={() => clickEditHandler(info.row.id)}>
                            <MDTypography><EditIcon /></MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Geofence">
                        <IconButton onClick={(e) => removeGeofenceHandler(e, info.row.id)}>
                            <MDTypography><DeleteIcon /></MDTypography>
                        </IconButton>
                    </Tooltip>
                </MDBox>
            )
        }
    ];

    const clickAddHandler = () => {
        navigate('/geofence-management/new')
    }

    const clickEditHandler = (id) => {
        navigate(`/geofence-management/edit/${id}`)
    }

    const removeGeofenceHandler = async (e, i) => {
        e.stopPropagation();
        try {
            if (!confirm("Are you sure you want to delete this geofence?")) {
                e.nativeEvent.stopImmediatePropagation();
            } else {
                let rem = await CrudService.deleteGeofence(i)
                if (rem.success) {
                    toast.success(rem.msg)
                    await getAllGeofences()
                } else {
                    toast.error(rem.error)
                }
            }
        } catch (error) {
            console.error('removeGeofenceHandler', error)
            toast.error(error.error);
        }
    }

    const handleMarkerClick = (geofence) => {
        zoomRef.current = map.getZoom();
        setSelectedGeofence(geofence);
        map?.panTo({ lat: parseFloat(geofence.latitude), lng: parseFloat(geofence.longitude) });
        map?.setZoom(16); // Zoom in on selected geofence
    };

    const handleTableRowClick = (id) => {
        try {
            let filterGeofence = geofences.filter((e) => e.geofence_id == id)

            if (filterGeofence.length > 0) {
                let geofence = filterGeofence[0]
                setSelectedGeofence(geofence);
                map?.panTo({ lat: parseFloat(geofence.latitude), lng: parseFloat(geofence.longitude) });
                map?.setZoom(16); // Zoom in on selected geofence
            }
        } catch (error) {
            console.error('handleTableRowClick', error)
        }
    }

    const handleCloseInfoWindow = () => {
        setSelectedGeofence(null);
        map?.setZoom(zoomRef.current ?? defaultZoom);
    };

    useEffect(() => {
        if (!clustererRef.current || !map) return;

        clustererRef.current.clearMarkers();
        const markerValues = Object.values(markers).filter(Boolean);
        if (markerValues.length > 0) {
            clustererRef.current.addMarkers(markerValues);
        }
    }, [markers, map]);

    const setMarkerRef = useCallback((marker, key) => {
        setMarkers((prevMarkers) => {
            if (!marker) {
                const { [key]: _, ...newMarkers } = prevMarkers;
                return newMarkers;
            }
            return { ...prevMarkers, [key]: marker };
        });
    }, []);

    return (
        <DashboardLayout>
            <Map
                style={containerStyle}
                defaultCenter={defaultCenter}
                defaultZoom={defaultZoom}
                mapId={'d089c1a7f8831a36'}
            >
                {[...(searchGeofences.length > 0 ? searchGeofences : geofences)].map((geofence, index) => {
                    const position = {
                        lat: parseFloat(geofence.latitude),
                        lng: parseFloat(geofence.longitude),
                    };

                    return (
                        <React.Fragment key={index}>
                            {/* <AdvancedMarker
                                position={position}
                                onClick={() => handleMarkerClick(geofence)}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <h6 style={{ margin: 0 }} >{geofence.name}</h6>
                                    <RoomIcon sx={{ color: 'red', fontSize: '30px !important' }} />
                                </div>
                            </AdvancedMarker> */}
                            <AdvancedMarkerWithRefLM key={index} geofence={geofence} position={position} onClick={handleMarkerClick} setMarkerRef={setMarkerRef} />

                            {selectedGeofence && selectedGeofence.geofence_id === geofence.geofence_id && (
                                <InfoWindow
                                    position={position}
                                    onCloseClick={handleCloseInfoWindow}
                                >
                                    <div>
                                        <h3>{geofence.name}</h3>
                                        <p style={{ marginBottom: '0.2rem' }}><b>Type:</b> {geofence.geofence_type}</p>
                                        <p style={{ marginBottom: '0.2rem' }}><b>Latitude:</b> {geofence.latitude}</p>
                                        <p style={{ marginBottom: '0.2rem' }}><b>Longitude:</b> {geofence.longitude}</p>
                                        {geofence.radius && <p style={{ marginBottom: '0.2rem' }}><b>Radius:</b> {geofence.radius} meters</p>}
                                    </div>
                                </InfoWindow>
                            )}

                            {geofence.geofence_type === "circle" && (
                                <CircleOverlay
                                    center={position}
                                    radius={geofence.radius}
                                    options={{
                                        fillColor: geofence.color,
                                        fillOpacity: 0.3,
                                        strokeColor: geofence.color,
                                        strokeWeight: 2,
                                    }}
                                />
                            )}

                            {geofence.geofence_type === "rectangle" && geofence.coordinates.length === 2 && (
                                <RectangleOverlay
                                    bounds={{
                                        north: Math.max(geofence.coordinates[0].lat, geofence.coordinates[1].lat),
                                        south: Math.min(geofence.coordinates[0].lat, geofence.coordinates[1].lat),
                                        east: Math.max(geofence.coordinates[0].lng, geofence.coordinates[1].lng),
                                        west: Math.min(geofence.coordinates[0].lng, geofence.coordinates[1].lng),
                                    }}
                                    options={{
                                        fillColor: geofence.color,
                                        fillOpacity: 0.3,
                                        strokeColor: geofence.color,
                                        strokeWeight: 2,
                                    }}
                                />
                            )}

                            {geofence.geofence_type === "polygon" && (
                                <PolygonOverlay
                                    paths={geofence.coordinates}
                                    options={{
                                        fillColor: geofence.color,
                                        fillOpacity: 0.3,
                                        strokeColor: geofence.color,
                                        strokeWeight: 2,
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Map>
            <MDBox pt={1}>
                <MDBox>
                    <Card>
                        <MDBox
                            p={3}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                Geofence Management
                            </MDTypography>
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    type="submit"
                                    onClick={clickAddHandler}
                                >
                                    + Add Geofence
                                </MDButton>
                                <MDInput
                                    type="text"
                                    label="Search"
                                    variant="outlined"
                                    onChange={(e) => searchHandler(e.target.value)}
                                    value={searchQuery}
                                />
                            </MDBox>
                        </MDBox>
                        <MDBox sx={{ height: `calc(100vh - 14rem)` }} >
                            <DataGrid
                                rows={tableData}
                                columns={columns}
                                pagination
                                paginationMode="server"
                                sortingMode="server"
                                filterMode="server"
                                rowCount={rowCount}
                                paginationModel={paginationModel}
                                sortModel={sortModel}
                                filterModel={filterModel}
                                columnVisibilityModel={columnVisibilityModel}
                                pageSizeOptions={[25, 50, 100]}
                                onSortModelChange={(model) => setSortModel(model)}
                                onFilterModelChange={(model) => setFilterModel(model)}
                                onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
                                onRowSelectionModelChange={(e) => {
                                    if (e.length > 0) handleTableRowClick(e[0]);
                                }}
                                onPaginationModelChange={(model) => setPaginationModel(model)}
                                loading={loader}
                                disableSelectionOnClick
                                sx={{ color: '#fff' }}
                                initialState={{ pinnedColumns: { left: ['id'] } }}
                            />
                        </MDBox>
                    </Card>
                </MDBox>
            </MDBox>
        </DashboardLayout>
    );
};

const AdvancedMarkerWithRefLM = ({ geofence, position, onClick, setMarkerRef }) => {
    const handleClick = useCallback(() => onClick(geofence), [onClick, geofence]);
    const ref = useCallback(
        (marker) => {
            setMarkerRef(marker, geofence.geofence_id);
        },
        [setMarkerRef, geofence.geofence_id]
    );

    return (
        <AdvancedMarker
            position={position}
            onClick={handleClick}
            ref={ref}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <h6 style={{ margin: 0 }} >{geofence.name}</h6>
                <RoomIcon sx={{ color: 'red', fontSize: '30px !important' }} />
            </div>
        </AdvancedMarker>
    )
}

export default GeofenceManager;